import { createContext, useState, useContext, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { getFamilyTreeFromDatabase, saveFamilyTreeToDatabase } from "../hooks/api";

const FamilyTreeRoomContext = createContext();

export const FamilyTreeRoomProvider = ({ children }) => {
  const [familyTreeData, setFamilyTreeData] = useState(() => {
    const stored = localStorage.getItem("childFamilyTree");
    return stored ? JSON.parse(stored) : null;
  });

  const isHydratedRef = useRef(false);
  // Expuesto a los consumidores: distingue "todavía cargando" de "cargó y no había nada guardado"
  const [isLoaded, setIsLoaded] = useState(false);

  const loadFamilyTree = useRef((childId) => {
    isHydratedRef.current = false;
    setIsLoaded(false);
    getFamilyTreeFromDatabase(childId)
      .then((data) => {
        // Confiar siempre en lo que devuelve el backend (aunque venga vacío) — si no, un
        // niño sin árbol todavía hereda el árbol cacheado de otro niño que se logueó antes
        // en el mismo dispositivo (misma key de localStorage sin child_id).
        setFamilyTreeData(data);
        if (data) {
          localStorage.setItem("childFamilyTree", JSON.stringify(data));
        } else {
          localStorage.removeItem("childFamilyTree");
        }
      })
      .catch((err) => console.error("No se pudo cargar el árbol familiar:", err))
      .finally(() => { isHydratedRef.current = true; setIsLoaded(true); });
  });

  useEffect(() => {
    const childId = localStorage.getItem("childId");
    if (!childId) return;
    loadFamilyTree.current(childId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStorage.getItem("childId")]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== "childId") return;
      if (e.newValue) {
        loadFamilyTree.current(e.newValue);
      } else {
        isHydratedRef.current = false;
        setIsLoaded(false);
        setFamilyTreeData(null);
        localStorage.removeItem("childFamilyTree");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (familyTreeData) {
      try {
        localStorage.setItem("childFamilyTree", JSON.stringify(familyTreeData));
      } catch (err) {
        // Cuota de localStorage llena (ej. una foto muy pesada) — no bloquea el guardado real en el backend
        console.error("No se pudo cachear el árbol familiar en localStorage:", err);
      }
    }
    if (!isHydratedRef.current || !familyTreeData) return;
    const childId = localStorage.getItem("childId");
    if (!childId) return;
    saveFamilyTreeToDatabase(familyTreeData)
      .catch((err) => console.error("Error al guardar el árbol familiar:", err));
  }, [familyTreeData]);

  const value = useMemo(() => ({ familyTreeData, setFamilyTreeData, isLoaded }), [familyTreeData, isLoaded]);

  return (
    <FamilyTreeRoomContext.Provider value={value}>
      {children}
    </FamilyTreeRoomContext.Provider>
  );
};

FamilyTreeRoomProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useFamilyTreeRoom = () => useContext(FamilyTreeRoomContext);
